import { h } from 'preact';
import Store from 'store/store';
import * as BaseComp from 'components/bolt/header/header.js';

import { UniversalLogo, NavDownIcon, LinkOutArrow, GlobeIcon } from 'helpers/customSvgs.js';
import { getPageFromIdOrPath, getRelativePageUrl, navigateInternally } from 'utils/bolt';
import { getAltLangMovieUrl } from 'utils/langSwitcher';
import { joinClasses, sortObjectByOrder, accessNested } from 'utils/utils';

import s from 'components/bolt/header/header.sss';

export default class Header extends BaseComp.default {

	constructor(props) {
		super(props);
		this.onBurgerClick = this.onBurgerClick.bind(this);
		this.checkMaxPrimaryLinks = this.checkMaxPrimaryLinks.bind(this);
		this.navItems = sortObjectByOrder(accessNested(props, 'appData.header.powsterHeader.nav'));
		this.standardLinks = this.navItems.filter(item => item && item.linkType === 'primary');
		this.featuredLinks = this.navItems.filter(item => item && item.linkType === 'secondary');
	}

	componentDidMount() {
		super.componentDidMount();
		this.checkMaxPrimaryLinks();
		this.getLangSwitcherUrl();
	}

	componentDidUpdate(prevProps, prevState) {
		const deviceWidthSize = this.props?.client?.deviceWidthSize;
		const orientation = this.props?.client?.orientation;
		if (prevProps?.client?.deviceWidthSize !== deviceWidthSize || prevProps?.client?.orientation !== orientation) {
			this.checkMaxPrimaryLinks();
		}
	}

	checkMaxPrimaryLinks() {
		const isMobile = this.props?.client?.deviceWidthSize === 'mobile' && this.props?.client?.orientation === 'portrait';
		if (isMobile) {
			this.setState({
				primaryLinks: this.standardLinks,
				secondaryLinks: this.featuredLinks
			});
			return;
		}
		const headerAppData = this.props?.appData?.header?.powsterHeader || {};
		const isDesktopWidth = this.props?.client?.deviceWidthSize?.toLowerCase()?.includes('desktop');
		const isSmallDesktop = this.props?.client?.deviceWidthSize === 'smallDesktop';

		const maxLinks = +(
			isSmallDesktop ? headerAppData.maxLinksSmallDesktop :
				isDesktopWidth ? headerAppData.maxLinksDesktop :
					headerAppData.maxLinksTablet
		);
		let primaryLinks = this.standardLinks || [];
		let secondaryLinks = this.featuredLinks || [];

		if (primaryLinks.length > maxLinks) {
			primaryLinks = this.standardLinks.slice(0, maxLinks);
			secondaryLinks = [
				...this.standardLinks.slice(maxLinks),
				...secondaryLinks
			];
		}

		this.setState({
			primaryLinks,
			secondaryLinks
		});
	}

	async getLangSwitcherUrl() {
		const langSwitcherUrl = await getAltLangMovieUrl() || null;
		this.setState({ langSwitcherUrl });
	}

	onBurgerClick() {
		const isHeaderOpen = this.props?.headerData?.open;
		if (!isHeaderOpen) {
			this.openNavMenu();
		} else {
			Store.emit('CLOSE_NAV_MENU');
		}
	}

	trackNavClick(label, path) {
		Store.emit('TRACK_NAV_LINK_CLICK', label, path);
	}

	onNavButtonClick(e, path) {
		e.preventDefault();
		navigateInternally(path);
		this.trackNavClick(e.currentTarget?.textContent, path);
		Store.emit('CLOSE_NAV_MENU');
	}

	renderNavItem(link, opts = {}) {
		const copy = this.props?.appData?.copy?.baseCopy || {};
		const label = copy?.[link.name] || link.name;
		if (/^(https?|mailto):\/\//i.test(link.path)) {
			return (
				<a
					class={s.navLink}
					href={link.path}
					target="_blank"
					onClick={() => this.trackNavClick(label, link.path)}
				>
					{label}
					{opts.showLinkOutIcon && <LinkOutArrow class={s.linkOutIcon} />}
				</a>
			);
		} else if (link.path) {
			let url = link.path;
			const page = getPageFromIdOrPath(link.path);
			if (page) {
				const [, queryString, hash] = link.path.match(/(\?[^#]*)?(#.*)?$/) || [];
				url = getRelativePageUrl(page.id, queryString, hash) || '#';
			}
			return (
				<a class={s.navButton} href={url} onClick={(e) => this.onNavButtonClick(e, link.path)}>
					{label}
				</a>
			);
		}
	}

	renderMobileFullMenu() {
		const mobileMenuOpen = this.props?.headerData?.open;
		const { primaryLinks, secondaryLinks } = this.state;

		return (
			<div class={joinClasses(s.mobileFullMenu, mobileMenuOpen && s.open)}>
				{primaryLinks?.length > 0 && (
					<div class={s.primaryLinks}>
						{primaryLinks.map(link => this.renderNavItem(link))}
					</div>
				)}
				{secondaryLinks?.length > 0 && (
					<div class={s.secondaryLinks}>
						{secondaryLinks.map(link => this.renderNavItem(link))}
					</div>
				)}

			</div>
		);
	}

	renderNavBarLinks() {
		const { primaryLinks, secondaryLinks } = this.state;
		const copy = this.props?.appData?.copy?.baseCopy || {};

		return (
			<div class={s.navBarLinks}>
				<div class={s.separator} />
				{primaryLinks?.length > 0 && primaryLinks.map(link => this.renderNavItem(link))}
				{secondaryLinks?.length > 0 && (
					<div
						class={s.moreExpander}
					>
						<button class={s.moreButton}>
							<span>{copy.$NAV_MORE}</span>
							<NavDownIcon class={s.moreIcon} />
						</button>
						<div class={s.moreExpandedMenu}>
							{secondaryLinks.map(link => this.renderNavItem(link, { showLinkOutIcon: true }))}
						</div>
					</div>
				)}
			</div>
		);
	}

	renderLangSwitcher() {
		const langSwitcherUrl = this.state.langSwitcherUrl;
		if (!langSwitcherUrl) return null;
		return (
			<a href={langSwitcherUrl} class={s.langSwitcher}>
				{this.props?.appData?.copy?.baseCopy?.$NAV_LANG_SWITCHER}
				<GlobeIcon />
			</a>
		);
	}

	render() {
		const client = this.props?.client;
		const isMobileOrTablet = !client?.isDesktopSize;
		const isMobile = client?.deviceWidthSize === 'mobile' && client?.orientation === 'portrait';

		const headerData = this.props?.headerData;
		const interstitialOpen = headerData?.interstitialOpen;
		const mobileMenuOpen = headerData?.open;

		const hub = this.props.appData?.meta?.hubUrl;

		return (
			<div id="header" class={joinClasses(s.container, isMobileOrTablet && interstitialOpen && s.hide)}>
				{isMobile && this.renderMobileFullMenu()}
				<div class={joinClasses(s.headerBar, isMobile && s.mobile)}>
					{isMobile && (
						<button class={joinClasses(s.menuBtn, mobileMenuOpen && s.menuOpen)} onClick={this.onBurgerClick}>
							<div class={s.burgerLine} />
							<div class={s.burgerLine} />
							<div class={s.burgerLine} />
							<div class={s.burgerLine} />
						</button>
					)}
					<a
						href={hub || '/'}
						class={s.logoLink}
						aria-label="Universal Pictures Home"
					>
						<UniversalLogo class={s.logo} />
					</a>
					{!isMobile && this.renderNavBarLinks()}
					<div class={s.actions}>
						{this.renderShare()}
						{this.renderLangSwitcher()}
					</div>
				</div>
			</div>
		);
	}

}
